import { SearchOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import {
  DonorStatus,
  useGetPhlebotomistStatsQuery,
  useGetRegionsQuery,
} from 'gql/types';
import { FC, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useOperationsDashboardContext } from '../../contexts/OperationsDashboardContext';
import { getDateRangeBounds } from '../../contexts/PhlebotomistDashboardContext/usePhlebotomistStatsData';
import { GET_RECRUITER_LOCATION_STATS_QUERY } from '../../graphql/queries';
import { useRecruiterDashboardMetrics } from '../../hooks/useRecruiterDashboardMetrics';
import { useRecruiterStats } from '../../hooks/useRecruiterStats';
import { buildOpsDrilldownUrl } from '../../utils/buildOpsDrilldownUrl';
import {
  computeLocationAttnScore,
  computeLocationHealthFlags,
  computeMaxAttnScore,
} from '../../utils/healthFlags';
import styles from '../OperationsDashboard/ops-theme.module.css';
import OpsLocationStrip, { ILocationStripEntry } from '../OpsLocationStrip';
import OpsPeriodFilter from '../OpsPeriodFilter';
import OpsRecruiterHeader from '../OpsRecruiterHeader';
import OpsRecruiterSummaryCards from '../OpsRecruiterSummaryCards';

const DEFAULT_ACTIVE_STATUSES = [
  DonorStatus.Active,
  DonorStatus.Approved,
  DonorStatus.AnnualLabworkDue,
];

/** Aggregate phlebotomist stats array into summary numbers */
function aggregatePhleboStats(
  stats: readonly {
    attemptedDonations: number;
    successfulDonations: number;
    actualTotal: number;
    actualPRBCSingle: number;
    actualFFPSingle: number;
    actualPRBCDouble: number;
    actualFFPDouble: number;
  }[]
) {
  if (!stats || stats.length === 0) {
    return {
      successRate: undefined as number | undefined,
      actualTotal: 0,
      prbcTotal: 0,
      sglTotal: 0,
      dblTotal: 0,
      attemptedDonations: 0,
      successfulDonations: 0,
    };
  }
  const agg = stats.reduce(
    (acc, s) => ({
      attemptedDonations: acc.attemptedDonations + s.attemptedDonations,
      successfulDonations: acc.successfulDonations + s.successfulDonations,
      actualTotal: acc.actualTotal + s.actualTotal,
      prbcTotal: acc.prbcTotal + s.actualPRBCSingle + s.actualPRBCDouble,
      sglTotal: acc.sglTotal + s.actualPRBCSingle + s.actualFFPSingle,
      dblTotal: acc.dblTotal + s.actualPRBCDouble + s.actualFFPDouble,
    }),
    {
      attemptedDonations: 0,
      successfulDonations: 0,
      actualTotal: 0,
      prbcTotal: 0,
      sglTotal: 0,
      dblTotal: 0,
    }
  );
  const successRate =
    agg.attemptedDonations > 0
      ? (agg.successfulDonations / agg.attemptedDonations) * 100
      : 0;
  return { ...agg, successRate };
}

const OpsRecruiterTab: FC = () => {
  const {
    recruiterFilters,
    setRecruiterDateRange,
    setRecruiterRegionId,
    setRecruiterId,
    recruiterSettings,
    opsSettings,
  } = useOperationsDashboardContext();

  const navigate = useNavigate();

  const { data: regionsData } = useGetRegionsQuery();
  const regions = useMemo(
    () =>
      regionsData?.getRegions?.map((r) => ({
        id: r.id,
        name: r.name,
        idexxAccountNumber: r.idexxAccountNumber,
        zoetisAccountNumber: r.zoetisAccountNumber,
      })) || [],
    [regionsData]
  );

  const regionOptions = useMemo(
    () => regions.map((r) => ({ id: r.id, name: r.name })),
    [regions]
  );

  const rSettings = recruiterSettings as Record<string, unknown> | null;

  // Fetch recruiter stats to populate recruiter dropdown and header
  const activeLocationThresholdDays = (rSettings?.activeLocationThresholdDays ??
    90) as number;

  const { entries } = useRecruiterStats({
    dateRange: recruiterFilters.dateRange,
    regionIds: recruiterFilters.regionId
      ? [recruiterFilters.regionId]
      : undefined,
    activeLocationThresholdDays,
  });

  const recruiters = useMemo(
    () =>
      entries.map((e) => ({
        id: e.userId,
        name: `${e.firstName} ${e.lastName}`,
        regionId: e.regionId,
      })),
    [entries]
  );

  // When a recruiter is selected, lock region to their region;
  // when cleared (All Recruiters), reset region to All Regions
  const handleRecruiterChange = useCallback(
    (id: string | undefined) => {
      setRecruiterId(id);
    },
    [setRecruiterId]
  );

  // Active donor statuses: prefer ops settings, fall back to recruiter settings
  const activeDonorStatuses = useMemo(
    () =>
      (opsSettings.activeDonorStatuses?.length
        ? opsSettings.activeDonorStatuses
        : undefined) ??
      (rSettings?.activeDonorStatuses as DonorStatus[] | undefined) ??
      DEFAULT_ACTIVE_STATUSES,
    [opsSettings.activeDonorStatuses, rSettings]
  );

  // Date bounds for period-scoped queries
  const dateBounds = useMemo(
    () =>
      getDateRangeBounds(
        recruiterFilters.dateRange,
        recruiterFilters.customDateRange
      ),
    [recruiterFilters.dateRange, recruiterFilters.customDateRange]
  );

  // Days in period
  const daysInPeriod = useMemo(() => {
    const ms = dateBounds.end.getTime() - dateBounds.start.getTime();
    return Math.max(1, Math.round(ms / (1000 * 60 * 60 * 24)));
  }, [dateBounds]);

  // Tables where we show all statuses (recruited donors can be any status)
  const ALL_STATUS_TABLES = new Set([
    'recruited',
    'summary:recruited',
    'newRecruits',
  ]);

  // Tables that have a fixed status defined in STATUS_FILTER_MAP —
  // never pass activeStatuses from ops settings so the map takes precedence
  const FIXED_STATUS_TABLES = new Set([
    'retired',
    'activeToRetired',
    'pending',
    'pipelineStall',
    'pendingToApproved',
    'pendingToArchived',
    'appliedToActive',
    'newRegistration',
  ]);

  // Navigate to drilldown tab with current filters + metric
  const handleDrilldown = useCallback(
    (metricKey: string, locationId?: string, locationName?: string) => {
      const url = buildOpsDrilldownUrl({
        dataTable: metricKey,
        locationId,
        locationName,
        dateRange: recruiterFilters.dateRange,
        customDateRange: recruiterFilters.customDateRange,
        regionId: recruiterFilters.regionId,
        recruiterId: recruiterFilters.recruiterId,
        activeStatuses:
          ALL_STATUS_TABLES.has(metricKey) || FIXED_STATUS_TABLES.has(metricKey)
            ? undefined
            : opsSettings.activeDonorStatuses,
        daysInPeriod:
          metricKey === 'summary:units' || metricKey === 'locationUnits'
            ? daysInPeriod
            : undefined,
      });
      navigate(url);
    },
    [recruiterFilters, opsSettings.activeDonorStatuses, daysInPeriod, navigate]
  );

  // Fetch all assigned locations (no threshold filter — inactive locations are
  // filtered client-side in locationStripEntries so summary totals include all data)
  const { data: locStatsData, loading: locStatsLoading } = useQuery(
    GET_RECRUITER_LOCATION_STATS_QUERY,
    {
      variables: {
        input: {
          recruiterId: recruiterFilters.recruiterId || undefined,
          activeDonorStatuses,
          regionIds: recruiterFilters.regionId
            ? [recruiterFilters.regionId]
            : undefined,
          dateFrom: dateBounds.start.toISOString(),
          dateTo: dateBounds.end.toISOString(),
          sglThresholdMl: opsSettings.sglThresholdMl ?? 420,
          unitsPerEventDays: opsSettings.unitsPerEventDays ?? 90,
        },
      },
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    }
  );

  const recruiterLocationIds = useMemo(() => {
    if (!recruiterFilters.recruiterId) return undefined;
    const locs = locStatsData?.getRecruiterLocationStats?.entries;
    if (!locs || locs.length === 0) return undefined;
    return locs.map((l: { locationId: string }) => l.locationId) as string[];
  }, [recruiterFilters.recruiterId, locStatsData]);

  // Compute header data
  const selectedRegion = useMemo(
    () => regions.find((r) => r.id === recruiterFilters.regionId),
    [regions, recruiterFilters.regionId]
  );

  const headerStats = useMemo(() => {
    const locs = locStatsData?.getRecruiterLocationStats?.entries;
    if (!locs) return { partnerLocationCount: 0 };
    const thresholdMs = activeLocationThresholdDays * 24 * 60 * 60 * 1000;
    const cutoff = Date.now() - thresholdMs;
    // When a recruiter is selected, only count their locations
    const filtered = recruiterFilters.recruiterId
      ? locs.filter((l: { locationId: string }) =>
          recruiterLocationIds?.includes(l.locationId)
        )
      : locs;
    const partnerLocationCount = filtered.filter(
      (l: { latestDonation?: string }) =>
        l.latestDonation
          ? new Date(l.latestDonation).getTime() >= cutoff
          : false
    ).length;
    return { partnerLocationCount };
  }, [
    locStatsData,
    activeLocationThresholdDays,
    recruiterFilters.recruiterId,
    recruiterLocationIds,
  ]);

  // ── Summary Card Data ──

  // Spatial filters: when recruiter selected, scope by their locations;
  // otherwise scope by region (or all)
  const regionIds = useMemo(
    () => (recruiterFilters.regionId ? [recruiterFilters.regionId] : undefined),
    [recruiterFilters.regionId]
  );

  const spatialFilter = useMemo(() => {
    if (recruiterLocationIds) {
      return { locationIds: recruiterLocationIds };
    }
    return { regionIds };
  }, [recruiterLocationIds, regionIds]);

  const { metrics: dashMetrics, loading: dashLoading } =
    useRecruiterDashboardMetrics({
      dateRange: recruiterFilters.dateRange,
      customDateRange: recruiterFilters.customDateRange,
      regionIds: spatialFilter.regionIds,
      locationIds: spatialFilter.locationIds,
      activeDonorStatuses,
      repeatDonationsThreshold: (rSettings?.repeatDonationsThreshold ??
        4) as number,
      donorDonationFrequency: (rSettings?.donorDonationFrequency ??
        14) as number,
      lastDonationActiveTimeoutDays:
        opsSettings.lastDonationActiveTimeoutDays ?? 90,
    });

  // Recruited count:
  // - Recruiter selected: sum per-recruiter attribution from entries
  // - Region selected: sum location stats (donors attributed to locations in that region)
  // - No filter: sum all location stats (unfiltered — all donors by createdAt)
  const { recruitedCount, prevRecruitedCount } = useMemo(() => {
    const locs = locStatsData?.getRecruiterLocationStats?.entries;
    if (!locs) return { recruitedCount: 0, prevRecruitedCount: 0 };
    // When recruiter selected, locStatsData is already scoped to their locations via recruiterId input
    // When region selected, scoped to that region's locations
    // When no filter, all locations — consistent with all other cards
    return {
      recruitedCount: locs.reduce(
        (sum: number, e: { recruitedCount?: number }) =>
          sum + (e.recruitedCount ?? 0),
        0
      ),
      prevRecruitedCount: locs.reduce(
        (sum: number, e: { prevRecruitedCount?: number }) =>
          sum + (e.prevRecruitedCount ?? 0),
        0
      ),
    };
  }, [locStatsData]);

  // ── Phlebotomist stats — current + previous period ──

  // Previous period = shift current bounds back by the period length
  const prevDateBounds = useMemo(() => {
    const durationMs = dateBounds.end.getTime() - dateBounds.start.getTime();
    const prevEnd = new Date(dateBounds.start.getTime() - 1);
    const prevStart = new Date(prevEnd.getTime() - durationMs);
    return { start: prevStart, end: prevEnd };
  }, [dateBounds]);

  // Current period phlebotomist stats
  const { data: phleboData, loading: phleboLoading } =
    useGetPhlebotomistStatsQuery({
      variables: {
        input: {
          dateFrom: dateBounds.start.toISOString(),
          dateTo: dateBounds.end.toISOString(),
          regionIds: spatialFilter.regionIds,
          locationIds: spatialFilter.locationIds,
        },
      },
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    });

  // Previous period phlebotomist stats
  const { data: prevPhleboData } = useGetPhlebotomistStatsQuery({
    variables: {
      input: {
        dateFrom: prevDateBounds.start.toISOString(),
        dateTo: prevDateBounds.end.toISOString(),
        regionIds: spatialFilter.regionIds,
        locationIds: spatialFilter.locationIds,
      },
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const phleboAgg = useMemo(
    () => aggregatePhleboStats(phleboData?.getPhlebotomistStats ?? []),
    [phleboData]
  );

  const prevPhleboAgg = useMemo(
    () => aggregatePhleboStats(prevPhleboData?.getPhlebotomistStats ?? []),
    [prevPhleboData]
  );

  const prevDaysInPeriod = useMemo(() => {
    const ms = prevDateBounds.end.getTime() - prevDateBounds.start.getTime();
    return Math.max(1, Math.round(ms / (1000 * 60 * 60 * 24)));
  }, [prevDateBounds]);

  // Expected units/day = dayPrbcGoal × number of regions represented
  // When a specific recruiter is selected, use 1 (single recruiter's goal)
  const dayPrbcGoal = (rSettings?.dayPrbcGoal ?? 25) as number;
  const regionCount = recruiterFilters.recruiterId
    ? 1
    : recruiterFilters.regionId
    ? 1
    : regions.length;
  const expectedPerDay = dayPrbcGoal * regionCount;

  // Previous period units/day avg
  // Total units / donor type counts from location stats (used for summary card + location strip)
  const locUnitsTotal = useMemo(() => {
    const locs = locStatsData?.getRecruiterLocationStats?.entries;
    if (!locs) return 0;
    return locs.reduce(
      (sum: number, e: { totalUnits?: number }) => sum + (e.totalUnits ?? 0),
      0
    );
  }, [locStatsData]);

  // Prior period units from location stats (consistent source with current period)
  const locPrevUnitsTotal = useMemo(() => {
    const locs = locStatsData?.getRecruiterLocationStats?.entries;
    if (!locs) return undefined;
    const total = locs.reduce(
      (sum: number, e: { prevPeriodUnits?: number }) =>
        sum + (e.prevPeriodUnits ?? 0),
      0
    );
    return total > 0 ? total : undefined;
  }, [locStatsData]);

  // Previous period units/day — use location stats for consistency with current period
  const prevUnitsPerDay =
    locPrevUnitsTotal !== undefined && prevDaysInPeriod > 0
      ? locPrevUnitsTotal / prevDaysInPeriod
      : undefined;

  const locSglDonorCount = useMemo(() => {
    const locs = locStatsData?.getRecruiterLocationStats?.entries;
    if (!locs) return 0;
    return locs.reduce(
      (sum: number, e: { sglDonorCount?: number }) =>
        sum + (e.sglDonorCount ?? 0),
      0
    );
  }, [locStatsData]);

  const locDblDonorCount = useMemo(() => {
    const locs = locStatsData?.getRecruiterLocationStats?.entries;
    if (!locs) return 0;
    return locs.reduce(
      (sum: number, e: { dblDonorCount?: number }) =>
        sum + (e.dblDonorCount ?? 0),
      0
    );
  }, [locStatsData]);

  // Summary success rate from location stats (matches location strip calculation)
  const locSuccessRate = useMemo(() => {
    const locs = locStatsData?.getRecruiterLocationStats?.entries;
    if (!locs) return undefined;
    const totals = locs.reduce(
      (
        acc: { attempted: number; successful: number },
        e: { attemptedDonations?: number; successfulDonations?: number }
      ) => ({
        attempted: acc.attempted + (e.attemptedDonations ?? 0),
        successful: acc.successful + (e.successfulDonations ?? 0),
      }),
      { attempted: 0, successful: 0 }
    );
    return totals.attempted > 0
      ? (totals.successful / totals.attempted) * 100
      : 0;
  }, [locStatsData]);

  const locPrevSuccessRate = useMemo(() => {
    const locs = locStatsData?.getRecruiterLocationStats?.entries;
    if (!locs) return undefined;
    const totals = locs.reduce(
      (
        acc: { attempted: number; successful: number },
        e: { prevAttemptedDonations?: number; prevSuccessfulDonations?: number }
      ) => ({
        attempted: acc.attempted + (e.prevAttemptedDonations ?? 0),
        successful: acc.successful + (e.prevSuccessfulDonations ?? 0),
      }),
      { attempted: 0, successful: 0 }
    );
    return totals.attempted > 0
      ? (totals.successful / totals.attempted) * 100
      : undefined;
  }, [locStatsData]);

  // ── Location strip data ──
  const locationStripEntries: ILocationStripEntry[] = useMemo(() => {
    const locs = locStatsData?.getRecruiterLocationStats?.entries;
    if (!locs) return [];
    const grandTotal = locUnitsTotal;
    const thresholds = opsSettings.healthFlagThresholds;
    const thresholdMs = activeLocationThresholdDays * 24 * 60 * 60 * 1000;
    const cutoff = Date.now() - thresholdMs;
    const activeLocs = locs.filter((l: { latestDonation?: string }) =>
      l.latestDonation ? new Date(l.latestDonation).getTime() >= cutoff : false
    );
    return activeLocs.map(
      (l: {
        locationId: string;
        locationName: string;
        regionName?: string;
        totalActiveDonors: number;
        latestDonation?: string;
        recruitedCount?: number;
        totalUnits?: number;
        successfulDonations?: number;
        attemptedDonations?: number;
        donationEventCount?: number;
        prevDonationEventCount?: number;
        pendingDonorCount?: number;
        sglPercent?: number;
        pendingAvgDays?: number;
        daysSinceLastRegistration?: number;
        prevPeriodUnits?: number;
        sglDonorCount?: number;
        dblDonorCount?: number;
        firstTimeDonationCount?: number;
        retiredDonorCount?: number;
        totalUnitsSinceInception?: number;
        firstDonationDate?: string;
        retiringIn30Count?: number;
        retiringIn60Count?: number;
        retiringIn90Count?: number;
        avgDaysAppliedToActive?: number;
        avgDaysPendingToApproved?: number;
        avgDaysActiveToRetired?: number;
        avgDaysPendingToArchived?: number;
        activeDonors90DaysAgo?: number;
        lastNewDonorName?: string;
        lastNewDonorDate?: string;
        stalledDonorsJson?: string;
      }) => {
        const units = l.totalUnits ?? 0;
        const eventCount = l.donationEventCount ?? 0;
        const alltimeUnits = (l as any).alltimeUnits ?? 0;
        const alltimeEvents = (l as any).alltimeEventCount ?? 0;
        // Units/Event:
        //   - unitsPerEventDays > 0: synthetic events = window / locationUnitsDays;
        //     upd = unitsInWindow / events.
        //   - unitsPerEventDays = 0 (all time): fall back to alltimeUnits / alltimeEvents.
        const upeWindow = opsSettings.unitsPerEventDays ?? 0;
        const eventFreqDays = opsSettings.locationUnitsDays || 29;
        let upd = 0;
        if (upeWindow > 0) {
          const syntheticEvents = upeWindow / eventFreqDays;
          if (syntheticEvents > 0) {
            upd = Math.round((alltimeUnits / syntheticEvents) * 10) / 10;
          }
        } else if (alltimeEvents > 0) {
          upd = Math.round((alltimeUnits / alltimeEvents) * 10) / 10;
        }
        const pctOfTotal =
          grandTotal > 0 ? Math.round((units / grandTotal) * 100) : 0;
        const lastDate = l.latestDonation
          ? new Date(l.latestDonation).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          : undefined;
        const totalPool = l.totalDonorPool ?? null;
        const healthFlags = computeLocationHealthFlags(
          {
            daysSinceLastRegistration: l.daysSinceLastRegistration,
            pendingAvgDays: l.pendingAvgDays,
            activeDonors: l.totalActiveDonors,
            currentUnits: (l as any).lastMonthUnits ?? 0,
            prevUnits: (l as any).prevMonthUnits ?? 0,
            lastNewDonorName: l.lastNewDonorName ?? undefined,
            lastNewDonorDate: l.lastNewDonorDate
              ? new Date(l.lastNewDonorDate)
              : undefined,
            stalledDonors: l.stalledDonorsJson
              ? (() => {
                  try {
                    return JSON.parse(l.stalledDonorsJson);
                  } catch {
                    return undefined;
                  }
                })()
              : undefined,
            totalDonorPool: l.totalDonorPool ?? undefined,
          },
          thresholds,
          totalPool
        );
        const recruited = l.recruitedCount ?? 0;
        const sglFrac = (l.sglPercent ?? 0) / 100;
        const sglCount = Math.round(recruited * sglFrac);
        const dblCount = recruited - sglCount;
        const successDon = l.successfulDonations ?? 0;
        const prevUnits = l.prevPeriodUnits ?? 0;
        const prevEventCount = l.prevDonationEventCount ?? 0;
        const prevUpd =
          prevEventCount > 0
            ? Math.round((prevUnits / prevEventCount) * 10) / 10
            : 0;
        // Success rate: successful donations / attempted donations (excluding ineligible)
        const attemptedDon = l.attemptedDonations ?? 0;
        const successRate =
          attemptedDon > 0
            ? Math.round((successDon / attemptedDon) * 1000) / 10
            : undefined;
        // Units per year: totalUnitsSinceInception / days since first donation * 365
        let unitsPerYear: number | undefined;
        if (l.totalUnitsSinceInception && l.firstDonationDate) {
          const firstDate = new Date(l.firstDonationDate);
          const daysSinceFirst = Math.max(
            1,
            (Date.now() - firstDate.getTime()) / (1000 * 60 * 60 * 24)
          );
          unitsPerYear = (l.totalUnitsSinceInception / daysSinceFirst) * 365;
        }
        return {
          locationId: l.locationId,
          locationName: l.locationName,
          regionName: l.regionName,
          activeDonors: l.totalActiveDonors,
          recruited,
          unitsPerDay: upd,
          unitsPerDayVsExp: `${pctOfTotal}% of total`,
          pending: l.pendingDonorCount ?? 0,
          pendingAvgDays: l.pendingAvgDays ?? undefined,
          sglPercent: l.sglPercent ?? undefined,
          healthFlags,
          attnScore: computeLocationAttnScore(
            l.locationId,
            healthFlags,
            opsSettings.attnScoreWeights
          ),
          lastEvent: lastDate ? `Last event: ${lastDate}` : undefined,
          periodLabel:
            recruiterFilters.dateRange === 'lastWeekCal'
              ? 'Week'
              : recruiterFilters.dateRange === 'lastMonthCal'
              ? 'Month'
              : recruiterFilters.dateRange === 'lastQuarterCal'
              ? 'Quarter'
              : recruiterFilters.dateRange === 'pYearCal'
              ? 'Year'
              : 'Period',
          dblCount,
          sglCount,
          successRate,
          attemptedDonations: attemptedDon > 0 ? attemptedDon : undefined,
          successfulDonations: successDon > 0 ? successDon : undefined,
          totalUnits: units > 0 ? units : undefined,
          donationEventCount: eventCount > 0 ? eventCount : undefined,
          prevUnitsPerDay: prevUpd > 0 ? prevUpd : undefined,
          dblDonorCount: l.dblDonorCount ?? 0,
          sglDonorCount: l.sglDonorCount ?? 0,
          firstTimeDonationCount: l.firstTimeDonationCount ?? 0,
          retiredDonorCount: l.retiredDonorCount ?? 0,
          unitsPerYear,
          retiringIn30Count: l.retiringIn30Count,
          retiringIn60Count: l.retiringIn60Count,
          retiringIn90Count: l.retiringIn90Count,
          avgDaysAppliedToActive: l.avgDaysAppliedToActive,
          avgDaysPendingToApproved: l.avgDaysPendingToApproved,
          avgDaysActiveToRetired: l.avgDaysActiveToRetired,
          avgDaysPendingToArchived: l.avgDaysPendingToArchived,
          activeDonors90DaysAgo: l.activeDonors90DaysAgo ?? undefined,
        };
      }
    );
  }, [
    locStatsData,
    daysInPeriod,
    opsSettings.healthFlagThresholds,
    opsSettings.attnScoreWeights,
    recruiterFilters.dateRange,
    activeLocationThresholdDays,
  ]);

  const flagCounts = useMemo(() => {
    let up = 0;
    let warn = 0;
    let down = 0;
    let locationsNeedAttention = 0;
    for (const loc of locationStripEntries) {
      let locHasDown = false;
      for (const f of loc.healthFlags) {
        if (f.status === 'up') up++;
        else if (f.status === 'warn') warn++;
        else {
          down++;
          locHasDown = true;
        }
      }
      if (locHasDown) locationsNeedAttention++;
    }
    return { up, warn, down, locationsNeedAttention };
  }, [locationStripEntries]);

  type LocationSortKey =
    | 'activeDonors'
    | 'recruited'
    | 'retiring'
    | 'retired'
    | 'unitsPerDay'
    | 'pending'
    | 'attnScore';

  const [locationSearch, setLocationSearch] = useState('');
  const [locationSortKey, setLocationSortKey] =
    useState<LocationSortKey>('attnScore');
  const [locationSortDir, setLocationSortDir] = useState<'desc' | 'asc'>(
    'desc'
  );

  const handleSortClick = useCallback(
    (key: LocationSortKey) => {
      if (key === locationSortKey) {
        setLocationSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
      } else {
        setLocationSortKey(key);
        setLocationSortDir('desc');
      }
    },
    [locationSortKey]
  );

  const displayedEntries = useMemo(() => {
    let result = locationStripEntries;
    if (locationSearch.trim()) {
      const q = locationSearch.trim().toLowerCase();
      result = result.filter((e) => e.locationName.toLowerCase().includes(q));
    }
    const dir = locationSortDir === 'desc' ? 1 : -1;
    return [...result].sort((a, b) => {
      switch (locationSortKey) {
        case 'activeDonors':
          return dir * (b.activeDonors - a.activeDonors);
        case 'recruited':
          return dir * (b.recruited - a.recruited);
        case 'retiring':
          return (
            dir *
            ((b.retiringIn30Count ?? 0) +
              (b.retiringIn60Count ?? 0) +
              (b.retiringIn90Count ?? 0) -
              ((a.retiringIn30Count ?? 0) +
                (a.retiringIn60Count ?? 0) +
                (a.retiringIn90Count ?? 0)))
          );
        case 'retired':
          return dir * (b.retiredDonorCount - a.retiredDonorCount);
        case 'unitsPerDay':
          return dir * (b.unitsPerDay - a.unitsPerDay);
        case 'pending':
          return dir * (b.pending - a.pending);
        case 'attnScore':
          return dir * (b.attnScore - a.attnScore);
        default:
          return 0;
      }
    });
  }, [locationStripEntries, locationSearch, locationSortKey, locationSortDir]);

  return (
    <div className={styles.animFadeUp}>
      <OpsPeriodFilter
        value={recruiterFilters.dateRange}
        customRange={recruiterFilters.customDateRange}
        onChange={setRecruiterDateRange}
        regions={regionOptions}
        regionId={recruiterFilters.regionId}
        onRegionChange={setRecruiterRegionId}
        regionLocked={!!recruiterFilters.recruiterId}
        recruiters={recruiters}
        recruiterId={recruiterFilters.recruiterId}
        onRecruiterChange={handleRecruiterChange}
      />
      <OpsRecruiterHeader
        regionCode={selectedRegion?.name || undefined}
        regionLocation={
          (recruiterFilters.regionId &&
            opsSettings.regionLocations[recruiterFilters.regionId]) ||
          undefined
        }
        partnerLocationCount={headerStats.partnerLocationCount}
        totalLocationCount={
          locStatsData?.getRecruiterLocationStats?.totalLocationCount ??
          locationStripEntries.length
        }
        flagsActiveCount={flagCounts.down}
        flagsUpCount={flagCounts.up}
        flagsWarnCount={flagCounts.warn}
        flagsDownCount={flagCounts.down}
        periodValue={recruiterFilters.dateRange}
      />
      <OpsRecruiterSummaryCards
        activeDonorCount={dashMetrics?.activeDonorCount ?? 0}
        historicalActiveDonorCount={dashMetrics?.historicalActiveDonorCount}
        comparedPeriodActiveDonorCount={
          dashMetrics?.comparedPeriodActiveDonorCount
        }
        activeDonorGrowthGoal={opsSettings.activeDonorGrowthGoal}
        recruitedCount={recruitedCount}
        prevRecruitedCount={prevRecruitedCount}
        unitsCollected={locUnitsTotal}
        prevUnitsCollected={locPrevUnitsTotal}
        daysInPeriod={daysInPeriod}
        prevDaysInPeriod={prevDaysInPeriod}
        successRate={locSuccessRate}
        prevSuccessRate={locPrevSuccessRate}
        expectedPerDay={expectedPerDay}
        prevUnitsPerDay={prevUnitsPerDay}
        sglTotal={locSglDonorCount}
        dblTotal={locDblDonorCount}
        selectedRegionName={selectedRegion?.name}
        periodValue={recruiterFilters.dateRange}
        loading={phleboLoading}
        metricsLoading={dashLoading}
        onDrilldown={(metricKey) => handleDrilldown(metricKey)}
      />
      <div className={styles.sectionBar}>
        <span className={styles.sectionBarTitle}>Partner Locations</span>
        <span className={styles.sectionBarAlert}>
          <span className={styles.sectionBarAlertCount}>
            {flagCounts.locationsNeedAttention}
          </span>{' '}
          Locations Need Attention
        </span>
      </div>
      <div className={styles.locationFilterRow}>
        <div className={styles.locationSearchWrap}>
          <SearchOutlined className={styles.locationSearchIcon} />
          <input
            type="text"
            className={styles.locationSearchInput}
            placeholder="Search locations…"
            value={locationSearch}
            onChange={(e) => setLocationSearch(e.target.value)}
          />
        </div>
        <div className={styles.locationSortGroup}>
          <span className={styles.locationSortLabel}>Order by</span>
          {(
            [
              ['activeDonors', 'Active Donors'],
              ['recruited', 'Recruited'],
              ['retiring', 'Retiring'],
              ['retired', 'Retired'],
              ['unitsPerDay', 'Units/Event'],
              ['pending', 'Pending'],
              ['attnScore', 'Attn Score'],
            ] as [LocationSortKey, string][]
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`${styles.locationSortBtn} ${
                locationSortKey === key
                  ? locationSortDir === 'asc'
                    ? styles.locationSortBtnAsc
                    : styles.locationSortBtnActive
                  : ''
              }`}
              onClick={() => handleSortClick(key)}
            >
              {label}
              {locationSortKey === key && (
                <span className={styles.locationSortDirIcon}>
                  {locationSortDir === 'desc' ? ' ▼' : ' ▲'}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
      <OpsLocationStrip
        entries={displayedEntries}
        loading={locStatsLoading}
        attnScoreThreshold={opsSettings.attnScoreThreshold}
        attnScoreMax={computeMaxAttnScore(opsSettings.attnScoreWeights)}
        locationActiveGrowthGoal={opsSettings.locationActiveGrowthGoal}
        locationPendingThreshold={opsSettings.locationPendingThreshold}
        onDrilldown={handleDrilldown}
      />
    </div>
  );
};

export default OpsRecruiterTab;
